import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs/Observable'
import { CrudSearchResult, CrudService } from './crud.service'
import { PersistentModel } from './persistent-model'
import { AuthService } from '../services'
import { QueryString } from '../models'

export abstract class StageDependantCrudService<T extends PersistentModel> extends CrudService<T> {

  protected constructor (
    protected http: HttpClient,
    protected urlSpecific: string,
    protected authService: AuthService,
    protected termOnly: boolean = false,
    typeDeserializer: (json: any) => T = (e) => e
  ) {
    super(http, urlSpecific, typeDeserializer)
  }

  public search (params?: QueryString): Observable<CrudSearchResult<T>> {
    this.updateUrl()
    return super.search(params)
  }

  public getById (id: string, params?: QueryString): Observable<T> {
    this.updateUrl()
    return super.getById(id, params)
  }

  getByIds = async (ids: string[]): Promise<T[]> => {
    const list: T[] = []

    for (const id of ids) {
      const element = (await promisify(this.getById(id)))
      list.push(element)
    }

    return list
  }

  public create (object: any): Observable<any> {
    this.updateUrl()
    return super.create(object)
  }

  public update (id: string, object: any): Observable<any> {
    this.updateUrl()
    return super.update(id, object)
  }

  public remove (id: string): Observable<any> {
    this.updateUrl()
    return super.remove(id)
  }

  protected updateUrl () {
    // A missing term/stage used to call safeLogout(), which wipes localStorage — a state bug
    // was signing the user out and killing the shared session. Only a genuinely invalid
    // session may log out now; anything else is surfaced as a normal request error.
    if (!this.authService.isLoggedIn()) {
      this.authService.safeLogout()
      throw new Error('Session expired — forcing logout')
    }
    const term = this.authService.getSelectedTerm()
    if (!term?._id) {
      throw new Error('No term selected')
    }
    this.url = `${this.apiUrl}/terms/${term._id}`
    if (!this.termOnly) {
      const stage = this.authService.getSelectedStage()
      if (!stage?._id) {
        throw new Error('No stage selected')
      }
      this.url += `/stages/${stage._id}`
    }
    this.url += `${this.urlSpecific}`
  }
}

function promisify<T>(observable: Observable<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    observable.subscribe(resolve, reject)
  })
}
